<script setup lang="ts">
  defineOptions({
    name: 'ChatMessageEditor',
  })

  /**
   * ChatMessageEditor - Full-featured chat message editor
   *
   * Features:
   * - TipTap editor with mentions support
   * - DRM upload for images/videos
   * - Document upload via useUpload
   * - GIF picker with direct send
   * - Emoji picker
   * - Drag and drop support
   * - Upload progress and status tracking
   * - File validation
   * - Video URL import (YouTube/Vimeo/Loom)
   */
  import { computed, defineAsyncComponent, ref, toRef, toValue } from 'vue'

  import { useI18n } from 'vue-i18n'
  import { AnalyticsEvents } from '@common/analytics-layer/types'

  import { Microphone01Icon, Send01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import EditorUploadCarousel from '@common/editor-layer/components/editor/EditorUploadCarousel.vue'

  import DragDropZone from '@common/shared-ui-layer/components/DragDropZone.vue'

  import { createElementId } from '@common/core-layer/utils/index'
  import { processDroppedFiles } from '@common/shared-ui-layer/utils/process-dropped-files'
  import { useNotificationHelper } from '@common/core-layer/utils/toast.utils'

  import CPDrawer from '@ui/components/CPDrawer.vue'

  import { useGroups } from '@communities/groups-layer/composables/useGroups'

  import CPButton from '@ui/components/CPButton.vue'

  import VoiceRecorderBar from '@common/media-layer/components/VoiceRecorderBar.vue'
  import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'

  import ChatMessageEditorInput from './ChatMessageEditorInput.vue'
  import ChatMessageEditorToolsPopover from './ChatMessageEditorToolsPopover.vue'

  import { useChatMessageEditor } from '../../composables/useChatMessageEditor'
  import { showChatMessageEditorValidationErrors } from '../../utils/chat-message-editor.helpers'
  import type { ChatMessageEditorInputExpose, RecordedVoiceMessage } from '../../types/chat-message-editor.types'

  import type { IChatAttachment } from '../../types/conversation.types'

  const GifPicker = defineAsyncComponent(() => import('@common/editor-layer/components/editor/tools/GifPicker.vue'))

  // ===== PROPS =====
  const props = defineProps({
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    maxImages: {
      type: Number,
      default: 10,
    },
    maxVideos: {
      type: Number,
      default: 2,
    },
    maxFiles: {
      type: Number,
      default: 2,
    },
    enableMentions: {
      type: Boolean,
      default: true,
    },
    locationId: {
      type: String,
      required: true,
    },
    groupId: {
      type: String,
      default: '',
    },
    useExternalDragDrop: {
      type: Boolean,
      default: false,
    },
  })

  // ===== EMITS =====
  const emit = defineEmits<{
    send: [payload: { text: string; html: string; attachments: IChatAttachment[] }]
    sendGif: [gif: { url: string; title: string }]
    voiceMessage: [payload: RecordedVoiceMessage]
    typing: []
    validationError: [errors: ValidationError[]]
  }>()

  const { t } = useI18n()
  const notification = useNotificationHelper()

  const { store: groupsStore } = useGroups()

  const inputRef = ref<ChatMessageEditorInputExpose | null>(null)

  const isSending = ref(false)

  const resolvedGroupId = computed(() => props.groupId || groupsStore.currentGroup?._id || '')

  const {
    validations,
    imageCount,
    videoCount,
    fileCount,
    hasAttachments,
    hasUploadingMedia,
    imagesForCarousel,
    videosForCarousel,
    gifsForCarousel,
    filesForCarousel,
    showToolsPopover,
    showGifPicker,
    showGifSheet,
    showVideoUploadModal,
    handleMediaFilesSelected,
    handleDocumentsSelected,
    handleVideoUrlImported,
    handleRemoveImage,
    handleRemoveVideo,
    handleRemoveGif,
    handleRemoveFile,
    handleFilesDropped,
    buildAttachments,
    resetUploads,
    voiceRecorder,
    voiceStage,
    voiceElapsedMs,
    voiceAmplitudes,
    voiceRecorderLabels,
    startVoiceRecordingFromMic,
    stopVoiceRecordingForSend,
    handleAudioFilesSelected,
    drmAppSource,
    drmEntityType,
    drmSourceEntityType,
    drmEntityId,
    drmSourceEntityId,
    DRMComponent,
    currentDRMFile,
    drmFileUploadRef,
    drmPlayRaw,
    handleDRMSuccess,
    handleDRMError,
    handleDRMProgress,
    handleDRMStatusChange,
  } = useChatMessageEditor({
    locationId: toRef(props, 'locationId'),
    groupId: resolvedGroupId,
    maxImages: toRef(props, 'maxImages'),
    maxVideos: toRef(props, 'maxVideos'),
    maxFiles: toRef(props, 'maxFiles'),
    onVoiceMessage: payload => emit('voiceMessage', payload),
  })

  const isRecordingVoice = computed(() => voiceStage.value !== 'idle')

  const mentionConfig = computed(() => ({
    enableUserMentions: props.enableMentions,
    enableHashMentions: false,
  }))

  const resolvedPlaceholder = computed(() => {
    return props.placeholder || t('chats.messageEditor.placeholder')
  })

  // Reactive text flag — the editor exposes text via getText() (not reactive),
  // so we track it on @change. Drives the WhatsApp-style mic/send swap and canSend.
  const hasText = ref(false)

  const hasContent = computed(() => hasText.value || hasAttachments.value)

  const canSend = computed(() => {
    return hasContent.value && !hasUploadingMedia.value && !isSending.value && !props.disabled
  })

  // Show send when there is something to send; otherwise show the voice mic.
  const showSendButton = computed(() => hasContent.value)

  /** Declarative GA binding — send intent at click time, not socket outcome. */
  const chatMessageSentTrack = computed(() => ({ event: AnalyticsEvents.CHAT_MESSAGE_SENT }))
  const showMicButton = computed(() => !hasContent.value)

  const handleValidationError = (errors: ValidationError[]) => {
    showChatMessageEditorValidationErrors(errors, t, notification)
    emit('validationError', errors)
  }

  const handleEditorPaste = async (event: ClipboardEvent) => {
    const files = Array.from(event.clipboardData?.files ?? [])
    if (files.length === 0) return

    event.preventDefault()
    event.stopPropagation()

    const { droppedFiles, errors } = await processDroppedFiles(files, toValue(validations))
    if (errors.length > 0) handleValidationError(errors)
    if (droppedFiles.length > 0) await handleFilesDropped(droppedFiles)
  }

  const handleEditorChange = () => {
    hasText.value = (inputRef.value?.getText() || '').trim().length > 0
    emit('typing')
  }

  const resetForm = () => {
    inputRef.value?.setContent('')
    hasText.value = false
    resetUploads()
  }

  const normalizeEditorHtml = (value: string) => {
    return value.replace(/<p[^>]*>\s+|\s+<\/p>/g, match => match.trim())
  }

  const handleSend = async () => {
    if (!canSend.value) return

    isSending.value = true

    try {
      const text = inputRef.value?.getText() || ''
      const html = normalizeEditorHtml(inputRef.value?.getHTML() || '')
      const attachments = buildAttachments(t('chats.messageEditor.attachments.gif'))

      emit('send', {
        text,
        html,
        attachments,
      })
    } finally {
      isSending.value = false
    }
  }

  const handleGifSend = (gif: { url: string; title: string }) => {
    emit('sendGif', gif)
    showGifPicker.value = false
    showGifSheet.value = false
    showToolsPopover.value = false
  }

  /** Stop mic → parent owns optimistic insert + upload + send. */
  const handleVoiceSend = async () => {
    const recorded = await stopVoiceRecordingForSend()
    if (recorded) emit('voiceMessage', recorded)
  }

  // Expose drag-drop related data for parent DragDropZone
  defineExpose({
    validations,
    imageCount,
    videoCount,
    fileCount,
    handleFilesDropped,
    handleValidationError,
    resetForm,
  })
</script>

<template>
  <div class="chat-message-editor">
    <component
      :is="useExternalDragDrop ? 'div' : DragDropZone"
      v-bind="
        useExternalDragDrop
          ? undefined
          : {
              variant: 'sm',
              validation: validations,
              overlayMessage: t('chats.messageEditor.dropFiles'),
            }
      "
      v-on="
        useExternalDragDrop
          ? {}
          : {
              filesDropped: handleFilesDropped,
              validationError: handleValidationError,
            }
      "
    >
      <EditorUploadCarousel
        v-if="hasAttachments"
        id="chat-editor-upload-carousel"
        :images="imagesForCarousel"
        :videos="videosForCarousel"
        :gifs="gifsForCarousel"
        :files="filesForCarousel"
        :group-id="resolvedGroupId"
        variant="sm"
        source="communities"
        @remove-image="handleRemoveImage"
        @remove-video="handleRemoveVideo"
        @remove-gif="handleRemoveGif"
        @remove-file="handleRemoveFile"
      />

      <VoiceRecorderBar
        v-if="isRecordingVoice"
        id="chat-message-editor-voice-bar"
        class="border-t border-default bg-background"
        id-prefix="voice-recorder"
        confirm-action="send"
        :stage="voiceStage"
        :elapsed-ms="voiceElapsedMs"
        :amplitudes="voiceAmplitudes"
        :labels="voiceRecorderLabels"
        @cancel="voiceRecorder.cancel"
        @toggle-pause="voiceRecorder.togglePause"
        @confirm="handleVoiceSend"
      />

      <div v-else class="flex items-center gap-2 border-t border-default bg-background p-3">
        <ChatMessageEditorToolsPopover
          :show-tools-popover="showToolsPopover"
          :show-gif-picker="showGifPicker"
          :show-gif-sheet="showGifSheet"
          :show-video-upload-modal="showVideoUploadModal"
          :disabled="disabled"
          :validations="validations"
          :current-image-count="imageCount"
          :current-video-count="videoCount"
          :current-file-count="fileCount"
          @update:showToolsPopover="showToolsPopover = $event"
          @update:showGifPicker="showGifPicker = $event"
          @update:showGifSheet="showGifSheet = $event"
          @update:showVideoUploadModal="showVideoUploadModal = $event"
          @mediaSelected="handleMediaFilesSelected"
          @documentsSelected="handleDocumentsSelected"
          @audioSelected="handleAudioFilesSelected"
          @gifSelected="handleGifSend"
          @videoUrlImported="handleVideoUrlImported"
          @validationError="handleValidationError"
        />

        <ChatMessageEditorInput
          id="chat-message-editor-input"
          ref="inputRef"
          :placeholder="resolvedPlaceholder"
          :disabled="disabled"
          :is-sending="isSending"
          :mention-config="mentionConfig"
          @paste="handleEditorPaste"
          @change="handleEditorChange"
          @submit="handleSend"
        />

        <CPButton
          v-if="showMicButton"
          id="chat-message-editor-voice-mic"
          type="text"
          size="sm"
          class="h-8 w-8 flex-none rounded-full !p-0"
          :disabled="disabled"
          :aria-label="t('chats.voiceNote.record')"
          @click="startVoiceRecordingFromMic"
        >
          <Microphone01Icon class="h-5 w-5" />
        </CPButton>

        <CPButton
          v-if="showSendButton"
          id="chat-message-editor-send"
          v-track="chatMessageSentTrack"
          type="primary"
          size="sm"
          class="h-8 w-8 rounded-full !p-0"
          :disabled="!canSend"
          @click="handleSend"
        >
          <Send01Icon class="h-4 w-4" />
        </CPButton>
      </div>

      <!-- GIF Bottom Sheet (mobile) -->
      <!-- z-index must exceed ChatContainer z-[10010] -->
      <CPDrawer
        :id="createElementId('chat-editor', 'mobile', 'gif-drawer')"
        :show="showGifSheet"
        placement="bottom"
        height="80vh"
        :closable="true"
        :show-header="false"
        :z-index="10011"
        body-class="p-0 m-0 flex items-center justify-center"
        @update:show="showGifSheet = $event"
      >
        <GifPicker width="100%" height="100%" :shadow="false" @gif-selected="handleGifSend" />
      </CPDrawer>

      <ClientOnly>
        <component
          :is="DRMComponent"
          v-if="DRMComponent && currentDRMFile"
          ref="drmFileUploadRef"
          :file="currentDRMFile"
          :app-source="drmAppSource"
          :entity-type="drmEntityType"
          :entity-id="drmEntityId"
          :source-entity-type="drmSourceEntityType"
          :source-entity-id="drmSourceEntityId || 'temp'"
          :type="currentDRMFile.type.includes('video') ? 'video' : 'image'"
          :play-raw="drmPlayRaw"
          :on-success="handleDRMSuccess"
          :on-error="handleDRMError"
          style="display: none"
          @progress-update="handleDRMProgress"
          @status-change="handleDRMStatusChange"
        />
      </ClientOnly>
    </component>
  </div>
</template>

<style scoped>
  .chat-message-editor {
    background: var(--cp-surface-500);
  }
</style>
